import Vue from "vue";
import {TinyEmitter} from "tiny-emitter";
import {analyticsController} from "src/modules/user/analytics/AnalyticsController";

// All false negative, (a lot of false negatives linked to WebMidi and TS
// @ts-ignore
// eslint-disable-next-line no-undef
type MIDIConnectionEvent = WebMidi.MIDIConnectionEvent;
// @ts-ignore
// eslint-disable-next-line no-undef
type MIDIMessageEvent = WebMidi.MIDIMessageEvent;
// @ts-ignore
// eslint-disable-next-line no-undef
type MIDIAccess = WebMidi.MIDIAccess;

import {isAnOffMessage, isAnOnMessage, MIDI_PITCH_TO_KEY_OFFSET, MidiMessageIndex} from './MidiUtils';
import {synthController} from "../synth/SynthController";
import {MidiKeyEvent} from './models/MidiKeyEvent';
import {isInIOSApp, isInMobileApp} from "src/utils/MobileUtils";
import { MidiConfigDevice } from "./models/MidiConfigDevice";
import * as Audio from "../audio"
interface MidiDispatcherState {
  isActive: boolean
  device: MidiConfigDevice | null
  activeKeys: number[]
}

class MidiDispatcher extends TinyEmitter {

  public readonly state = Vue.observable<MidiDispatcherState>({
    isActive: false,
    device: null,
    activeKeys: []
  });

  /* Setup */

  // constructor() {
  //   super()
  //   // TODO: Could use a better implementation than setTimeout using deviceready for instance.
  //   // Temporary disable midi permission check and add it to player
  //   // setTimeout(() => this.initMidiDispatcher(), 2000);
  // }

  initMidiDispatcher() {
    if (isInMobileApp() && isInIOSApp()) {
      this.initIOSMidi();
    }
    else {
      this.initWebMidiIfAvailable();
    }
  }

  /* Ios Midi Api */

  private initIOSMidi() {
    // TODO: Could use a better implementation with type definition, maybe check with capacitor ?
    // @ts-ignore
    // eslint-disable-next-line no-undef
    Midi.initListeners();
    // @ts-ignore
    document.addEventListener("iOSMidiDeviceChanged", (event) => midiDispatcher.onMidiStateChange(event), false);
    // @ts-ignore
    document.addEventListener("iOSMidiNoteReceived", (event) => midiDispatcher.onMidiMessageReceived(this, event.data), false);
  }

  /* Web Midi Api */
  private midiController: MIDIAccess|null = null;

  private initWebMidiIfAvailable() {
    if (navigator.requestMIDIAccess) {
      navigator.requestMIDIAccess({sysex: false}).then(
      // @ts-ignore
        (midiData: MIDIAccess) =>
          this.onMidiSuccess(this, midiData), MidiDispatcher.onMidiFailure
      )
    }
  }

  private listenToAllMidiControllerInputs() {
    const allInputs = this.midiController!.inputs.values();

    for (let input = allInputs.next(); input && !input.done; input = allInputs.next()) {
      input.value.onmidimessage = (message: MIDIMessageEvent) => midiDispatcher.onMidiMessageReceived(this, message.data);
    }
  }

  private onMidiSuccess(midiListenerStore: MidiDispatcher, midiData: MIDIAccess) {
    midiListenerStore.midiController = midiData;
    midiListenerStore.midiController.onstatechange = function(e: MIDIConnectionEvent) {
      midiDispatcher.onMidiStateChange(e.port);
      if (e.port.state === "connected") {
        midiListenerStore.listenToAllMidiControllerInputs();
      }
    };
    // TODO: should handle case where keyboard is already connected
    midiListenerStore.listenToAllMidiControllerInputs();
  }

  private static onMidiFailure() {
    console.log('Could not access your MIDI devices.');
  }

  /* Midi Listener */
  private onMidiStateChange(midiEvent: any) {
    this.state.device = {
      name: midiEvent.name,
      type: midiEvent.type,
      manufacturer: midiEvent.manufacturer,
      state: isInIOSApp() ? (midiEvent[0] === 1 ? 'connected' : 'disconnected') : midiEvent.state
    };
    this.state.isActive = midiEvent.state === 'connected';

    if(this.state.isActive) {
      Audio.musicTranscriptionController.terminate()
    }

    analyticsController.logAnalyticsEvent({
      title: (this.state.isActive) ? 'connected midi device' : 'unconnected midi device',
      category: 'Midi Events',
      gaLabelIdentifier: 'name',
      parameters: midiEvent
    });
  }

  public simulateMidiMessage(keys: number[]) {
    const dispatcherKeysWithoutKeys = this.state.activeKeys.filter((element) => !keys.includes(element));
    const keysWithoutDispatcherKeys = keys.filter((element) => !this.state.activeKeys.includes(element));

    dispatcherKeysWithoutKeys.forEach((key) => { // Get OFF messages
      console.log("OFF MESSAGES")
      const event: MidiKeyEvent = new MidiKeyEvent(key, 1);
      this.removeFromActiveNote(event)
      this.emit('notesStopped', event);
    })

    keysWithoutDispatcherKeys.forEach((key) => { // Get ON messages
      console.log("ON MESSAGES")
      const event: MidiKeyEvent = new MidiKeyEvent(key, 1);
      this.addToActiveNote(event)
      this.emit('notePlayed', event);
    })
  }

  public clearActiveKeys() {
    this.state.activeKeys.length = 0;
  }

  private onMidiMessageReceived(midiDispatcher: MidiDispatcher, messageData: Uint8Array) {
    const key = messageData[MidiMessageIndex.PITCH] + MIDI_PITCH_TO_KEY_OFFSET;
    const midiKeyEvent = new MidiKeyEvent(key, messageData[MidiMessageIndex.VELOCITY]);
    this.state.isActive = true
    this.state.device = {...this.state.device, state : 'connected'}

    if (isAnOnMessage(messageData)) {
      synthController.triggerAttack(midiKeyEvent);
      midiDispatcher.addToActiveNote(midiKeyEvent)
      midiDispatcher.emit('notePlayed', midiKeyEvent);
    }

    if (isAnOffMessage(messageData)) {
      synthController.triggerRelease(midiKeyEvent);
      midiDispatcher.removeFromActiveNote(midiKeyEvent)
      midiDispatcher.emit('notesStopped', midiKeyEvent);
    }

    midiDispatcher.emit('messageEvent', messageData);
  }

  /* Active Keys */

  private addToActiveNote(noteMessage: MidiKeyEvent) {
    if(!this.state.activeKeys.includes(noteMessage.key)) {
      this.state.activeKeys.push(noteMessage.key);
      this.emit('activeKeysChanged', this.state.activeKeys)
    }
  }

  private removeFromActiveNote(noteMessage: MidiKeyEvent) {
    this.state.activeKeys = this.state.activeKeys.filter(function (currentNote) {
      return currentNote !== noteMessage.key;
    });
    this.emit('activeKeysChanged', this.state.activeKeys)
  }
}

export const midiDispatcher = new MidiDispatcher();
